import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, ExternalLink, ArrowRight, ChevronRight } from "lucide-react";
import { api, auth } from "@/lib/api";
import { T } from "@/lib/testIds";
import { toast } from "sonner";

export default function Login() {
  const [managerId, setManagerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmed = managerId.trim();
    if (!/^\d+$/.test(trimmed)) {
      setError("Manager ID must be a whole number.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/link", { manager_id: Number(trimmed) });
      auth.setToken(data.token);
      toast.success(`Welcome ${data.manager.player_first_name || ""}!`);
      nav("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not link this manager ID.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-totem-bg text-totem-text">
      <div className="mx-auto max-w-md px-6 pt-14 pb-16">
        {/* Logo + brand */}
        <div className="flex flex-col items-start gap-3 mb-10">
          <div className="grid place-items-center w-16 h-16 rounded-2xl bg-totem-surfaceElev border border-totem-border">
            <Sparkles size={30} className="text-totem-green" strokeWidth={2.4} />
          </div>
          <div>
            <div className="mono uppercase text-[11px] tracking-[0.25em] text-totem-textDim">FPL</div>
            <h1 className="display-heading text-totem-green text-5xl -mt-1">TOTEM</h1>
            <div className="mono uppercase text-[10px] tracking-[0.3em] text-totem-textFaint mt-2">
              Fantasy Football Intelligence
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label
              htmlFor="mgr"
              className="block label-caps mb-2"
            >
              Manager ID
            </label>
            <input
              id="mgr"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 1234567"
              data-testid={T.loginManagerIdInput}
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              className="w-full bg-totem-surfaceElev border border-totem-border rounded-lg px-4 py-4 text-lg mono focus:outline-none focus:border-totem-green focus:ring-2 focus:ring-totem-green/25 transition-colors"
            />
            {error && (
              <p data-testid={T.loginError} className="mt-2 text-sm text-totem-red">
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid={T.loginSubmitButton}
            className="w-full flex items-center justify-center gap-2 bg-totem-green text-totem-bg font-semibold rounded-lg py-4 hover:brightness-110 disabled:opacity-60 transition"
          >
            {loading ? "Linking..." : "Link my team"}
            <ArrowRight size={18} strokeWidth={2.5} />
          </button>
        </form>

        {/* Explainer */}
        <div className="mt-10">
          <div className="label-caps mb-4">How to find your Manager ID</div>
          <ol className="space-y-3">
            {[
              {
                n: 1,
                t: "Log in to fantasy.premierleague.com",
                d: "Open the Points tab of your team.",
              },
              {
                n: 2,
                t: "Look at the URL",
                d: "It contains /entry/{YOUR_ID}/… The number between /entry/ and the next / is your Manager ID.",
              },
              {
                n: 3,
                t: "Paste it above",
                d: "We only need this public ID — never your FPL password.",
              },
            ].map((s) => (
              <li key={s.n} className="flex gap-3">
                <span className="mt-0.5 grid place-items-center w-7 h-7 rounded-full bg-totem-green/15 border border-totem-green/40 text-totem-green mono text-sm font-semibold shrink-0">
                  {s.n}
                </span>
                <div>
                  <div className="text-sm font-medium text-totem-text">{s.t}</div>
                  <div className="text-xs text-totem-textDim leading-relaxed">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>

          <a
            href="https://fantasy.premierleague.com"
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm text-totem-textDim hover:text-totem-green transition"
          >
            Open FPL <ExternalLink size={14} />
          </a>
        </div>

        <div className="mt-10 text-[11px] text-totem-textFaint mono">
          No password required. FPL Totem never asks for your FPL credentials.
        </div>
      </div>
    </div>
  );
}
